import React, { useState, Fragment } from "react";
import classnames from "classnames";
import Modal from "components/Modal";
import ModalContent from "components/ModalContent";
import ModalActions from "components/ModalActions";
import CellEntitlementHeader from "../CellEntitlementHeader";
import FeatureEntitlementsRow from "../FeatureEntitlementsRow";
import { Draggable } from "react-beautiful-dnd";
import { useFormikContext } from "formik";
import FeatureGroupForm from "../FeatureGroupForm";
import HoverIconButton from "components/HoverIconButton";
import { getRemoveFeatureGroupConfirm } from "utils/confirm";
import Analytics, {
  REMOVE_FEATURE_GROUP,
  UPDATE_FEATURE_GROUP
} from "utils/analytics";
import get from "lodash/get";
import { removeFeatureGroupFromEntitlements } from "utils/entitlements";

const FEATURE_GROUPS = "featureGroups";
const ENTITLEMENTS = "entitlements";

const TableGroupRow = ({
  tracking,
  index,
  products,
  featureGroup,
  features,
  thWidth,
  tdWidth,
  activeIndex
}) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [hoverCell, setHoverCell] = useState(false);
  const headProps = {
    className: classnames("tl", {
      "bl bb hairline-1": !index
    }),
    onMouseEnter: () => !hoverCell && setHoverCell(true),
    onMouseLeave: () => hoverCell && setHoverCell(false)
  };
  const featureGroupsTouched = Boolean(get(touched, FEATURE_GROUPS));
  const entitlementsTouched = Boolean(get(touched, ENTITLEMENTS));
  const headContentProps = {
    style: index
      ? {
          boxShadow: "0 0 0 1px rgb(233, 234, 236)"
        }
      : {},
    className: classnames("pv3 pr3 fw5 f4 f3-ns pointer w-100", {
      "mt5": index,
      "pl3-ns": !hoverCell
    })
  };
  const validFeatureUUIDs = featureGroup.feature_uuids.filter((uuid) =>
    features.find((feature) => feature.uuid === uuid)
  );

  const updateFeatureGroup = (featureGroupValues) => {
    Analytics.track(UPDATE_FEATURE_GROUP, tracking);
    const updatedFeatureGroup = { ...featureGroup, ...featureGroupValues };
    const updatePath = `${FEATURE_GROUPS}[${index}]`;
    setFieldValue(updatePath, updatedFeatureGroup);
    setFieldTouched(updatePath, true);
    setShowEditModal(false);
    setHoverCell(false);
  };

  /**
   * On update request submit
   * - Mark all features within the removed group for removal too
   */
  const removeFeatureGroup = () => {
    Analytics.track(REMOVE_FEATURE_GROUP, tracking);
    const updatedFeatureGroups = [...values.featureGroups];
    const groupIndex = updatedFeatureGroups.findIndex(
      ({ uuid }) => uuid === featureGroup.uuid
    );
    if (groupIndex >= 0) {
      const featureGroupMatch = updatedFeatureGroups[groupIndex];

      /**
       * 1. Remove FeatureGroup from collection
       */
      updatedFeatureGroups.splice(groupIndex, 1);
      setFieldValue(FEATURE_GROUPS, updatedFeatureGroups);
      if (!featureGroupsTouched) {
        setFieldTouched(FEATURE_GROUPS, true);
      }

      /**
       * 2. Remove all features within feature group from entitlements
       */
      const updatedEntitlements = removeFeatureGroupFromEntitlements({
        featureGroup: featureGroupMatch,
        features: values.features,
        entitlements: values.entitlements
      });

      if (updatedEntitlements) {
        setFieldValue(ENTITLEMENTS, updatedEntitlements);
        if (!entitlementsTouched) {
          setFieldTouched(ENTITLEMENTS, true);
        }
      }
    } else {
      console.error(
        `Invalid index for feature group removal with uuid ${featureGroup.uuid}`
      );
    }

    setShowDeleteModal(false);
    setHoverCell(false);
  };

  const removeButton = (
    <HoverIconButton
      onClick={() => setShowDeleteModal(true)}
      type="close"
      size={20}
    />
  );

  const removalHeader = getRemoveFeatureGroupConfirm({
    title: featureGroup.title,
    featureCount: validFeatureUUIDs.length
  });

  /**
   * These are the list of rows per feature group. Users can
   * - Drag and drop them vertically to re-order them
   * - Toggle the cells on / off and edit their values
   */
  const featureRowNodes = validFeatureUUIDs.reduce((memo, uuid, featureIx) => {
    const featureMatch = features.find((feature) => feature.uuid === uuid);

    if (featureMatch) {
      memo.push(
        <Draggable key={uuid} draggableId={uuid} index={featureIx}>
          {(provided, snapshot) => (
            <FeatureEntitlementsRow
              tracking={tracking}
              provided={provided}
              snapshot={snapshot}
              thWidth={thWidth}
              tdWidth={tdWidth}
              key={`groupRow-${featureGroup.uuid}-${uuid}`}
              feature={featureMatch}
              featureGroup={featureGroup}
              products={products}
              activeIndex={activeIndex}
            />
          )}
        </Draggable>
      );
    }
    return memo;
  }, []);

  /**
   * These are the column headers above the group of features
   * - Users can toggle all values in a column using these controls
   */
  const CellEntitlementHeaderNodes = products.map((product, productIx) => {
    return (
      <CellEntitlementHeader
        tracking={tracking}
        rowIndex={index}
        productIndex={productIx}
        activeIndex={activeIndex}
        isLast={productIx === products.length - 1}
        tdWidth={tdWidth}
        key={productIx}
        product={product}
        features={features}
        featureGroup={featureGroup}
      />
    );
  });

  return (
    <Fragment>
      <tr className="bn">
        <th {...headProps}>
          <div {...headContentProps}>
            <div className="flex flex-row items-center w-100">
              {hoverCell && (
                <div className="dn flex-ns h-100 items-center justify-center">
                  {removeButton}
                </div>
              )}
              <div className="flex dn-ns h-100 items-center justify-center">
                {removeButton}
              </div>
              <div
                onClick={() => {
                  setShowEditModal(true);
                  setHoverCell(false);
                }}
                className="w-100 link__underline"
              >
                {featureGroup.title}
              </div>
            </div>
          </div>
          <Modal
            key={`delete-${featureGroup.uuid}`}
            showModal={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
          >
            <ModalContent
              slides={[
                {
                  title: `Remove feature group`,
                  header: removalHeader
                }
              ]}
            >
              {() => (
                <div className="w-100 pa3">
                  <ModalActions
                    actions={[
                      {
                        cta: "Cancel",
                        onClick: () => {
                          setShowDeleteModal(false);
                        }
                      },
                      {
                        cta: "Yes",
                        onClick: removeFeatureGroup
                      }
                    ]}
                  />
                </div>
              )}
            </ModalContent>
          </Modal>
          <Modal
            key={`edit-${featureGroup.uuid}`}
            showModal={showEditModal}
            onClose={() => setShowEditModal(false)}
          >
            <FeatureGroupForm
              setShowModal={setShowEditModal}
              updateFeatureGroup={updateFeatureGroup}
              featureGroup={featureGroup}
            />
          </Modal>
        </th>
        {CellEntitlementHeaderNodes}
      </tr>
      {featureRowNodes}
    </Fragment>
  );
};

export default TableGroupRow;
